import { clipSvgWidth } from "common/utilities/svg/svg.ts";
import { isGatewayTimeoutResponse, showMessages } from "core/api/apiHelpers";
import type * as I from "core/api/coreApiClient";
import { getStore } from "core/redux/store/configureStore";
import I18n from "i18n/en-US";

export function dateTimeForFileName(date?: Date) {
  const d = date || new Date();

  return [
    d.getFullYear(),
    "-",
    `0${d.getMonth() + 1}`.slice(-2),
    "-",
    `0${d.getDate()}`.slice(-2),
    "_",
    `0${d.getHours()}`.slice(-2),
    "-",
    `0${d.getMinutes()}`.slice(-2),
    "-",
    `0${d.getSeconds()}`.slice(-2),
  ].join("");
}

export function buildFileName(prefix: string, extension: string, date?: Date) {
  return `${prefix}_${dateTimeForFileName(date)}.${extension}`;
}

async function downloadFileResponse(url: string) {
  const response = await fetch(url, { credentials: "include" });

  if (response.status === 400) {
    const body = (await response.json()) as I.ResponseWithMessages;
    showMessages(getStore(), body);
    throw new Error(I18n.utils.downloadFile.unable);
  } else if (response.status !== 200) {
    if (isGatewayTimeoutResponse(response.status)) {
      throw new Error(I18n.utils.downloadFile.errorGatewayTimeout);
    } else {
      throw new Error(I18n.utils.downloadFile.error);
    }
  }

  return response;
}

/**
 * Start a download within the browser
 * @param url of the file to download
 */
async function downloadFile(url: string) {
  const response = await downloadFileResponse(url);

  // Uncomment the following lines to test the modal
  // const testDownloadDelay = async () => sleep(5000);
  // await testDownloadDelay();

  // Handle special case where a Jnlp file should be opened right away and not downloaded
  if (!response.headers.get("Content-disposition") && url.match("analyzer=Jnlp")) {
    window.location.replace(url);

    setTimeout(() => {
      window.close();
    }, 500);

    return undefined;
  }

  // Create a link element, assign the response data blob and trigger the link to download the file
  const blob = await response.blob();
  const d = document.createElement("a");

  const filenameRegex = /filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/;
  let filename = ((response.headers.get("Content-disposition") || "").match(filenameRegex) || [])[1];
  if (!filename) {
    // response.url might be different from the request url, in case of redirects
    // cutting short any optional query params
    const downloadPath = (response.url || url).split("?")[0];
    filename = downloadPath.substring(downloadPath.lastIndexOf("/") + 1);
  }

  d.download = filename;
  d.href = URL.createObjectURL(blob);
  document.body.appendChild(d);
  d.onclick = (e) => e.stopPropagation(); // avoid the click to influence other listeners
  d.click();
  if (d.parentElement) {
    d.parentElement.removeChild(d);
  }

  return filename;
}

export default downloadFile;

export async function downloadFileArrayBuffer(url: string) {
  const response = await downloadFileResponse(url);
  const data = await response.arrayBuffer();
  return data;
}

export function downloadImgFile(filename: string, dataURL: string) {
  const link = document.createElement("a");
  link.href = dataURL;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function downloadCanvasFile(canvasEl: HTMLCanvasElement, fileName: string) {
  const copyCanvasEl = document.createElement("canvas");
  const { width, height } = canvasEl.getBoundingClientRect();

  const copyCanvasElCtx = copyCanvasEl.getContext("2d");

  if (copyCanvasElCtx) {
    copyCanvasEl.setAttribute("width", `${width}px`);
    copyCanvasEl.setAttribute("height", `${height}px`);

    // add white background
    copyCanvasElCtx.fillStyle = "white";
    copyCanvasElCtx.fillRect(0, 0, width, height);

    // draw the image onto the canvas
    copyCanvasElCtx.drawImage(canvasEl, 0, 0, width, height);

    // trigger canvas download as png
    const dataURL = copyCanvasEl.toDataURL("image/png", 1.0);
    downloadImgFile(buildFileName(fileName, "png"), dataURL);

    copyCanvasEl.remove();
  }
}

export function downloadSVGFile(svg: HTMLElement, filename: string) {
  const base64doc = btoa(unescape(encodeURIComponent(svg.outerHTML)));
  const a = document.createElement("a");
  const e = new MouseEvent("click");
  a.download = filename;
  a.href = `data:image/svg+xml;base64,${base64doc}`;
  a.dispatchEvent(e);
}
export function downloadSVGFileAsPNG(
  svg: SVGElement,
  fileName: string,
  start: number, // starting x-coordinate in pixels
  stop: number, // ending x-coordinate in pixels
  canvas?: HTMLCanvasElement,
  onDownloadEnd?: () => void,
) {
  const img = document.createElement("img");
  const canvasEl = canvas || document.createElement("canvas");

  const { svgClone, originalHeight, rangeWidth } = clipSvgWidth(svg, start, stop);

  // Serialize the modified SVG
  const xml = new XMLSerializer().serializeToString(svgClone);
  const svg64 = btoa(unescape(encodeURIComponent(xml)));
  const b64Start = "data:image/svg+xml;base64,";
  const image64 = b64Start + svg64;

  img.onload = function () {
    const ctx = canvasEl.getContext("2d");

    if (ctx) {
      // Set canvas dimensions to match the range
      canvasEl.setAttribute("width", `${rangeWidth}px`);
      canvasEl.setAttribute("height", `${originalHeight}px`);

      // Add white background
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, rangeWidth, originalHeight);

      // Draw the image onto the canvas
      ctx.drawImage(img, 0, 0, rangeWidth, originalHeight);

      // Trigger canvas download as PNG
      const dataURL = canvasEl.toDataURL("image/png", 1.0);
      downloadImgFile(fileName, dataURL);

      img.remove();
      if (!canvas) canvasEl.remove();

      if (onDownloadEnd) onDownloadEnd();
    }
  };

  img.src = image64;
}

// Alternative: If you want to work with column positions instead of pixel coordinates
export function downloadSVGFileAsPNGByColumns(
  svg: SVGElement,
  fileName: string,
  startColumn: number, // starting column index
  stopColumn: number, // ending column index
  columnWidth: number, // width per column
  canvas?: HTMLCanvasElement,
  onDownloadEnd?: () => void,
) {
  const startPixel = startColumn * columnWidth;
  const stopPixel = stopColumn * columnWidth;

  return downloadSVGFileAsPNG(svg, fileName, startPixel, stopPixel, canvas, onDownloadEnd);
}

export function downloadTextFile(filename: string, filecontent: string) {
  const link = document.createElement("a");
  const blob = new Blob([filecontent]);
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
